/**
 * Your picture.
 *
 * Four things this has to get right, because they are the four things that go
 * wrong on a phone in a kitchen:
 *
 *  1. TOO BIG — a modern phone photo is often 6–12MB. The size and the type are
 *     checked in the browser BEFORE a byte is uploaded, so a kid on a slow
 *     connection gets an instant, plain answer instead of a 30-second wait and a
 *     413. The Worker checks again; that check is the rule, this one is the
 *     kindness.
 *  2. HEIC — the iPhone camera default, and no browser but Safari will paint it.
 *     It is refused, here and in the Worker, with the sentence that fixes it on
 *     the phone. Storing one would put a broken-image icon in the most prominent
 *     element on the page.
 *  3. FAILED — an upload that fails says so and leaves the old picture exactly
 *     where it was. A failure is never rendered as a success.
 *  4. EMPTY — no picture is a normal state, not a hole: it is the initial in
 *     their color, the same thing the header draws.
 *
 * The upload is immediate (not held behind the Save bar), because a file picker
 * that quietly does nothing until you scroll down and press another button is how
 * you lose a nine-year-old.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type { Profile } from '../../shared/types';
import { AlertNote, Button, InitialAvatar, cx } from './ui';

const MAX_BYTES = 5 * 1024 * 1024; // keep in step with worker/routes/profile.ts

/** Formats a browser will actually paint. Keep in step with worker/routes/profile.ts. */
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/** Refused, not accepted-and-then-unshowable. Same list as the Worker's. */
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_HELP =
  'iPhone HEIC photos don’t show up in web browsers. On your iPhone: Settings → Camera → Formats → “Most Compatible”, then take the photo again — or open the photo, tap Edit, and save a copy as a JPEG.';

function mb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export default function AvatarField({
  profile,
  name,
  color,
  onSaved,
}: {
  profile: Profile;
  /** The draft name/color, so the fallback initial updates as they pick. */
  name: string;
  color: string;
  onSaved: (next: Profile) => void;
}) {
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  /** Bumped by "Try again" to re-request the image past the browser's cache. */
  const [attempt, setAttempt] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Object URLs are a leak if they outlive the component.
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  // A new (or removed) picture is a fresh chance to render. Whatever went wrong with
  // the last one is not a fact about this one.
  useEffect(() => {
    setBroken(false);
  }, [profile.avatar_url]);

  const remoteSrc = profile.avatar_url
    ? `${profile.avatar_url}${profile.avatar_url.includes('?') ? '&' : '?'}try=${attempt}`
    : null;
  const src = preview ?? remoteSrc;
  const hasPhoto = Boolean(profile.avatar_url);

  async function pick(file: File) {
    setError(null);

    const type = file.type.toLowerCase();

    if (HEIC_TYPES.includes(type)) {
      setError(HEIC_HELP);
      return;
    }
    if (!TYPES.includes(type)) {
      setError('That file is not a picture this app can show. Use a JPEG, PNG, WEBP or GIF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(
        `That picture is ${mb(file.size)} — a bit big. Pictures need to be under 5MB, so pick a smaller one or take a new photo.`
      );
      return;
    }
    if (file.size === 0) {
      setError('That file is empty.');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);
    setBroken(false);
    setBusy('upload');

    try {
      const form = new FormData();
      form.append('avatar', file);
      const next = await api.upload<Profile>('/profile/avatar', form);
      onSaved(next);
      // The server now has it, and its URL carries a fresh ?v= key, so the local
      // preview can go.
      setPreview(null);
      URL.revokeObjectURL(url);
    } catch (e) {
      // Keep the old picture. Do not pretend this worked.
      setPreview(null);
      URL.revokeObjectURL(url);
      setError(
        e instanceof ApiRequestError
          ? e.message
          : 'That picture did not upload. Your old one is still here — try again in a second.'
      );
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setError(null);
    setBusy('remove');
    try {
      const next = await api.del<Profile>('/profile/avatar');
      onSaved(next);
      setBroken(false);
    } catch (e) {
      setError(
        e instanceof ApiRequestError
          ? e.message
          : 'That did not save. Your picture is still here — try again in a second.'
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-5">
        <div className="relative">
          {src && !broken ? (
            <img
              src={src}
              alt={`${name || 'Your'} profile picture`}
              onError={() => setBroken(true)}
              className="h-24 w-24 rounded-full border-2 border-border object-cover"
              style={{ borderColor: color }}
            />
          ) : (
            <InitialAvatar
              name={name}
              color={color}
              className="h-24 w-24 border-2 border-border text-3xl"
            />
          )}

          {busy === 'upload' && (
            <span className="absolute inset-0 grid place-items-center rounded-full bg-ink/50 text-xs font-semibold text-white">
              Saving…
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          {/* A styled label IS the file input's button: it keeps the native picker
              (camera roll, take-a-photo) instead of reimplementing it badly. */}
          <label
            className={cx(
              'inline-flex min-h-12 cursor-pointer items-center justify-center rounded-full bg-sage px-5 text-base font-medium text-white transition-colors hover:bg-sage/90',
              'focus-within:ring-2 focus-within:ring-sage focus-within:ring-offset-2 focus-within:ring-offset-surface',
              busy && 'pointer-events-none opacity-60'
            )}
          >
            {hasPhoto ? 'Change picture' : 'Add a picture'}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              disabled={busy !== null}
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first, so picking the SAME file twice still fires a change.
                e.target.value = '';
                if (file) void pick(file);
              }}
            />
          </label>

          {hasPhoto && (
            <Button
              variant="ghost"
              onClick={() => void remove()}
              disabled={busy !== null}
              className="justify-start px-3"
            >
              {busy === 'remove' ? 'Removing…' : 'Remove picture'}
            </Button>
          )}

          <p className="text-xs text-ink-soft">
            {hasPhoto
              ? 'JPEG, PNG, WEBP or GIF, up to 5MB. Take it off any time and you’re back to your initial.'
              : 'No picture yet — that’s your initial, in your color. JPEG, PNG, WEBP or GIF, up to 5MB.'}
          </p>
        </div>
      </div>

      {/* The <img> failed. That is ALL we know: an onError fires the same way for a
          missing object, an expired Access session and a dropped connection, and we
          cannot see which from here. So say what happened, not why, and give them the
          two moves that might actually fix it. */}
      {broken && src && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-sunk px-4 py-3">
          <p className="min-w-0 flex-1 text-xs text-ink-soft">
            Your picture didn’t load. We can’t tell why from here — it could be the
            connection, or the picture may no longer be there. Your initial is showing
            for now.
          </p>
          {hasPhoto && (
            <Button
              variant="secondary"
              className="min-h-11 px-4 text-sm"
              onClick={() => {
                setBroken(false);
                setAttempt((n) => n + 1);
              }}
            >
              Try again
            </Button>
          )}
        </div>
      )}

      {error && <AlertNote message={error} />}
    </div>
  );
}
